import React from 'react';
import { MenuItem, Typography, Menu, Button } from '@mui/material';
import { menuItemSx, listMenuItemSx } from './menuItemSx';
import { getExternalClientPermissions, hasExternalClientPermission } from '../../helpers/externalClientPermissions';

interface NavigationContentProps {
    labUserCheck: any;
    selectedClientId: number | null;
    linkTo: (page: string) => void;
    standardQuote: boolean;
    rolePermission: boolean;
    isSyncData: boolean;
    navigate: any;
    isMobile?: boolean;
    // Menu state props
    dashboardMenuAnchor: any;
    quoteMenuBar: any;
    catalogueMenuBar: any;
    adminMenuBar: any;
    orderMenuAnchor: any;
    // Menu handlers
    handleDashboardMenu: (e: any) => void;
    handleDashboardClose: () => void;
    handleOrderMenu: (e: any) => void;
    handleOrderClose: () => void;
    handleMenu: (e: any) => void;
    handleClose: () => void;
    handleCatalogueMenu: (e: any) => void;
    handleCatalogueClose: () => void;
    handleAdminClick: (e: any) => void;
    handleAdminClose: () => void;
    // Action handlers
    handleQuoteWithOutClient: () => void;
    handleQuoteWithClient: () => void;
    handleViewQuote: () => void;
    handleCatalogue: (name: string) => void;
    menuKey: number;
}

export const NavigationContent: React.FC<NavigationContentProps> = ({
    labUserCheck,
    selectedClientId,
    linkTo,
    standardQuote,
    rolePermission,
    isSyncData,
    navigate,
    isMobile = false,
    dashboardMenuAnchor,
    quoteMenuBar,
    catalogueMenuBar,
    adminMenuBar,
    orderMenuAnchor,
    handleDashboardMenu,
    handleDashboardClose,
    handleOrderMenu,
    handleOrderClose,
    handleMenu,
    handleClose,
    handleCatalogueMenu,
    handleCatalogueClose,
    handleAdminClick,
    handleAdminClose,
    handleQuoteWithOutClient,
    handleQuoteWithClient,
    handleViewQuote,
    handleCatalogue,
    menuKey
}) => {
    const isExternalUser = labUserCheck?.UserType === 'External';
    const externalClientPermissions = getExternalClientPermissions(labUserCheck?.ClientPermissions);
    const canManageExternalUsers = hasExternalClientPermission(externalClientPermissions, ['ManageAllUsers', 'ManageUsers']);
    const canCreateExternalQuotes = hasExternalClientPermission(externalClientPermissions, 'Quotes');
    const canPlaceExternalOrders = hasExternalClientPermission(externalClientPermissions, 'BottleOrders');
    const permissions: string[] = Array.isArray(labUserCheck?.Permission)
        ? labUserCheck.Permission
        : labUserCheck?.Permission
            ? [labUserCheck.Permission]
            : [];

    const hasPermission = (required: string | readonly string[]) => {
        if (typeof required === 'string') {
            return permissions.includes(required);
        }
        return required.some((perm) => permissions.includes(perm));
    };

    const menuPermissions = {
        order: ['ViewContainerOrder', 'EditContainerOrder'],
        quote: ['StandardQuotes'],
        analytics: ['Analytics'],
        adminUserManagement: ['AddUser', 'ManageClientUsers'],
        internalUserManagement: ['ManageInternalUsers'],
        catalogue: ['SyncData', 'CompareTest', 'StandardQuotes'],
        syncData: ['SyncData'],
        releaseNotesHistory: ['AddReleaseNotes'],
        adminNewClientRequest: ['ReviewOnboardRequest', 'ActionOnboardRequest'],
    } as const;

    const menuPermissionValues: string[] = Object.values(menuPermissions).flat().map(String);
    const shouldRestrictMenus = permissions.some((perm) =>
        menuPermissionValues.includes(perm)
    );

    const canShowOrderMenu = labUserCheck?.UserType === 'Internal' && hasPermission(menuPermissions.order);
    const canShowQuoteMenu = hasPermission(menuPermissions.quote);
    const canShowAnalyticsMenu = hasPermission(menuPermissions.analytics);
    const canShowCatalogueMenu = hasPermission(menuPermissions.catalogue);
    const canShowAdminUserManagement = hasPermission(menuPermissions.adminUserManagement);
    const canShowAdminNewClientRequest = hasPermission(menuPermissions.adminNewClientRequest);
    const canShowInternalUserManagement = hasPermission(menuPermissions.internalUserManagement);
    const canShowSyncData = hasPermission(menuPermissions.syncData);
    const canShowReleaseNotesHistory = hasPermission(menuPermissions.releaseNotesHistory);

    const renderHomeButton = () => {
        if (shouldRestrictMenus) return null;
        if (labUserCheck && labUserCheck.UserType !== 'Internal') {
            return isMobile ? (
                <MenuItem onClick={() => linkTo('home')}>
                    <Typography variant="body2" sx={menuItemSx}>HOME</Typography>
                </MenuItem>
            ) : (
                <Button
                    onClick={() => linkTo('home')}
                    sx={menuItemSx}
                >
                    HOME
                </Button>
            );
        }
        return null;
    };

    const renderReportsMenu = () => {
        if (shouldRestrictMenus && !canShowAnalyticsMenu) return null;
        // Only show ANALYTICS for Internal users
        if (labUserCheck?.UserType !== 'Internal') {
            return null;
        }

        return (
            <>
                {isMobile ? (
                    <MenuItem onClick={handleDashboardMenu}>
                        <Typography variant="body2" sx={menuItemSx}>ANALYTICS</Typography>
                    </MenuItem>
                ) : (
                    <Button
                        onClick={handleDashboardMenu}
                        sx={menuItemSx}
                    >
                        ANALYTICS
                    </Button>
                )}
                <Menu
                    anchorEl={dashboardMenuAnchor}
                    open={Boolean(dashboardMenuAnchor)}
                    onClose={handleDashboardClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={listMenuItemSx}
                >
                    <MenuItem onClick={() => { linkTo('home/dashboard'); handleDashboardClose(); }}>
                        <Typography variant="body2" sx={menuItemSx}>Reports Overview</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => { linkTo('home/analytics'); handleDashboardClose(); }}>
                        <Typography variant="body2" sx={menuItemSx}>Sample Data</Typography>
                    </MenuItem>
                </Menu>
            </>
        );
    };

    const renderQuoteMenu = () => {
        if (isExternalUser && !canCreateExternalQuotes) return null;
        if (shouldRestrictMenus && !canShowQuoteMenu) return null;
        // For non-Internal users, render button that directly navigates to clientView
        if (labUserCheck?.UserType !== 'Internal') {
            return isMobile ? (
                <MenuItem onClick={() => linkTo('home/clientView')}>
                    <Typography variant="body2" sx={menuItemSx}>QUOTE</Typography>
                </MenuItem>
            ) : (
                <Button
                    onClick={() => linkTo('home/clientView')}
                    sx={menuItemSx}
                >
                    QUOTE
                </Button>
            );
        }

        // For Internal users, render button with menu
        if (labUserCheck?.UserType === 'Internal' && standardQuote) {
            return (
                <>
                    {isMobile ? (
                        <MenuItem onClick={handleMenu}>
                            <Typography variant="body2" sx={menuItemSx}>QUOTE</Typography>
                        </MenuItem>
                    ) : (
                        <Button
                            onClick={handleMenu}
                            sx={menuItemSx}
                        >
                            QUOTE
                        </Button>
                    )}
                    <Menu
                        key={menuKey}
                        anchorEl={quoteMenuBar}
                        open={Boolean(quoteMenuBar)}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        sx={listMenuItemSx}
                    >
                        <MenuItem onClick={() => handleQuoteWithOutClient()}>
                            <Typography variant="body2" sx={menuItemSx}>Generate Quote</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => handleQuoteWithClient()}>
                            <Typography variant="body2" sx={menuItemSx}>Generate Quote for a Client</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => handleViewQuote()} >
                            <Typography variant="body2" sx={menuItemSx}>View Quotes</Typography>
                        </MenuItem>
                    </Menu>
                </>
            );
        }
    };

    const renderCatalogueMenu = () => {
        if (shouldRestrictMenus && !canShowCatalogueMenu) return null;
        // Only show CATALOGUE for Internal users
        if (labUserCheck?.UserType !== 'Internal') {
            return null;
        }

        return (
            <>
                {isMobile ? (
                    <MenuItem onClick={handleCatalogueMenu}>
                        <Typography variant="body2" sx={menuItemSx}>CATALOGUE</Typography>
                    </MenuItem>
                ) : (
                    <Button
                        onClick={handleCatalogueMenu}
                        sx={menuItemSx}
                    >
                        CATALOGUE
                    </Button>
                )}
                <Menu
                    anchorEl={catalogueMenuBar}
                    open={Boolean(catalogueMenuBar)}
                    onClose={handleCatalogueClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={listMenuItemSx}
                >
                    <MenuItem onClick={() => { handleCatalogue('price'); handleCatalogueClose(); }}>
                        <Typography variant="body2" sx={menuItemSx}>Price List Catalogue</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => { handleCatalogue('compare'); handleCatalogueClose(); }}>
                        <Typography variant="body2" sx={menuItemSx}>Compare Test</Typography>
                    </MenuItem>
                </Menu>
            </>
        );
    };

    const renderOrderButton = () => {
        // Only show ORDER section for Internal users
        // if (labUserCheck?.UserType !== 'Internal') return null;
        if (isExternalUser && !canPlaceExternalOrders) return null;
        if (shouldRestrictMenus && !canShowOrderMenu) return null;

        return (
            <>
                {isMobile ? (
                    <MenuItem onClick={handleOrderMenu}>
                        <Typography variant="body2" sx={menuItemSx}>ORDER</Typography>
                    </MenuItem>
                ) : (
                    <Button onClick={handleOrderMenu} sx={menuItemSx}>
                        ORDER
                    </Button>
                )}
                <Menu
                    anchorEl={orderMenuAnchor}
                    open={Boolean(orderMenuAnchor)}
                    onClose={handleOrderClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={listMenuItemSx}
                >
                    <MenuItem onClick={() => { navigate('/home/orderHistory'); handleOrderClose(); }}>
                        <Typography variant="body2" sx={menuItemSx}>Order History</Typography>
                    </MenuItem>
                    {!isExternalUser && (
                        <MenuItem onClick={() => { navigate('/home/inventory'); handleOrderClose(); }}>
                            <Typography variant="body2" sx={menuItemSx}>Inventory</Typography>
                        </MenuItem>
                    )}
                </Menu>
            </>
        );
    };

    const renderAdminMenu = () => {
        const shouldShowAdmin = labUserCheck && (
            (isExternalUser && canManageExternalUsers) ||
            (labUserCheck.UserType === 'Internal' && rolePermission)
        );

        if (shouldRestrictMenus && !canShowAdminUserManagement && !canShowAdminNewClientRequest) return null;
        if (!shouldRestrictMenus && !shouldShowAdmin) return null;

        return (
            <>
                {isMobile ? (
                    <MenuItem onClick={handleAdminClick}>
                        <Typography variant="body2" sx={menuItemSx}>ADMIN</Typography>
                    </MenuItem>
                ) : (
                    <Button
                        onClick={handleAdminClick}
                        sx={menuItemSx}
                    >
                        ADMIN
                    </Button>
                )}
                <Menu
                    anchorEl={adminMenuBar}
                    open={Boolean(adminMenuBar)}
                    onClose={handleAdminClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={listMenuItemSx}
                >
                    {shouldRestrictMenus ? (
                        <>
                            {(labUserCheck?.UserType === 'Internal' && canShowAdminUserManagement) && <MenuItem onClick={() => { navigate(`/home/admin`); handleAdminClose(); }}><Typography variant="body2" sx={menuItemSx}>User Management</Typography></MenuItem>}
                            {(labUserCheck?.UserType === 'Internal' && canShowInternalUserManagement) && <MenuItem onClick={() => { navigate(`/home/internal`); handleAdminClose(); }}><Typography variant="body2" sx={menuItemSx}>Internal User Management</Typography></MenuItem>}
                            {labUserCheck?.UserType === 'Internal' && canShowAdminNewClientRequest && <MenuItem onClick={() => { navigate(`/home/new-client-request`); handleAdminClose(); }}><Typography variant="body2" sx={menuItemSx}>New Client Request</Typography></MenuItem>}
                            {labUserCheck?.UserType === 'Internal' && canShowSyncData && <MenuItem onClick={() => { navigate(`/home/syncData`); handleAdminClose(); }}><Typography variant="body2" sx={menuItemSx}>Library Sync</Typography></MenuItem>}
                            {labUserCheck?.UserType === 'Internal' && canShowReleaseNotesHistory && <MenuItem onClick={() => { navigate(`/home/releaseNotesHistory`); handleAdminClose(); }}><Typography variant="body2" sx={menuItemSx}>Release Notes History</Typography></MenuItem>}
                        </>
                    )
                        : (
                            <>
                                {isExternalUser && canManageExternalUsers && <MenuItem onClick={() => { navigate(`/home/admin`); handleAdminClose(); }}><Typography variant="body2" sx={menuItemSx}>User Management</Typography></MenuItem>}
                            </>)}
                </Menu>
            </>
        );
    };

    return (
        <>
            {renderHomeButton()}
            {renderReportsMenu()}
            {renderQuoteMenu()}
            {renderCatalogueMenu()}
            {renderOrderButton()}
            {renderAdminMenu()}
        </>
    );
};